import React, { useEffect, useState } from "react";
import "../../styles/clients.css";
import { Plus, Edit, Trash2, RefreshCw, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { supabase } from "../../api/supabaseClient";
import { confirmAndDelete } from "../../utils/deleteHelper";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const EMPTY_FORM = {
    owner_id: null,
    owner_name: "",
    mobile: "",
    designation: "",
};

const Owners = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    // ── Pagination ───────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data: owners = [], loading, fetchAll: fetchOwners, save: saveOwner, remove: deleteOwner } = useGeneric({
        tableName: "owners",
        idColumn: "owner_id",
        orderBy: "owner_name",
        removeOnInsert: ["owner_id"],
    });

    useEffect(() => { fetchOwners(); }, [fetchOwners]);
    useEffect(() => { setCurrentPage(1); }, [search, pageSize]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        const name = form.owner_name.trim();
        const mobile = form.mobile.trim();

        if (!name) errs.owner_name = "Owner name is required.";
        else if (name.length > 50) errs.owner_name = "Max 50 characters.";

        if (!mobile) errs.mobile = "Mobile number is required.";
        else if (!/^\d{10}$/.test(mobile)) errs.mobile = "Mobile must be 10 digits.";

        if ((form.designation || "").trim().length > 50) errs.designation = "Max 50 characters.";

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Duplicate mobile check (client import matches owners by mobile) ──
    const mobileExists = async (mobile, excludeId) => {
        let query = supabase
            .from("owners")
            .select("owner_id, owner_name")
            .eq("mobile", mobile)
            .limit(1);
        if (excludeId) query = query.neq("owner_id", excludeId);
        const { data, error } = await query;
        if (error) throw new Error("Mobile check failed: " + error.message);
        return data && data.length > 0 ? data[0] : null;
    };

    // ── Save ─────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const mobile = form.mobile.trim();
            const existing = await mobileExists(mobile, form.owner_id);
            if (existing) {
                setErrors((prev) => ({
                    ...prev,
                    mobile: `Mobile number already exists (Owner: ${existing.owner_name}).`,
                }));
                return;
            }

            await saveOwner({
                owner_id: form.owner_id,
                owner_name: form.owner_name.trim(),
                mobile,
                designation: (form.designation || "").trim() || null,
            });
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            if (err?.code === "23505") {
                setErrors((prev) => ({ ...prev, mobile: "Mobile number already exists." }));
            } else {
                alert("Save failed: " + (err.message || "Unknown error"));
            }
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => { setForm(EMPTY_FORM); setErrors({}); setShowForm(true); };

    const openEdit = (owner) => {
        setForm({
            owner_id: owner.owner_id,
            owner_name: owner.owner_name ?? "",
            mobile: owner.mobile ?? "",
            designation: owner.designation ?? "",
        });
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => { setShowForm(false); setForm(EMPTY_FORM); setErrors({}); };

    // ── Delete (blocked while any client still points to this owner) ──
    const handleDelete = async (owner, e) => {
        e?.stopPropagation();

        const { count, error } = await supabase
            .from("agencies")
            .select("agency_id", { count: "exact", head: true })
            .eq("owner_id", owner.owner_id);
        if (error) { alert("Could not check linked clients: " + error.message); return; }
        if (count > 0) {
            alert(
                `Cannot delete "${owner.owner_name}" because ${count} client${count > 1 ? "s are" : " is"} ` +
                `linked to this owner. Change the owner on those clients first.`
            );
            return;
        }

        await confirmAndDelete({
            what: "owner",
            name: owner.owner_name,
            usedIn: "clients",
            run: () => deleteOwner(owner.owner_id),
        });
    };

    // ── Filter & sort ────────────────────────────────────────────────
    const q = search.trim().toLowerCase();
    const filtered = (owners || [])
        .filter((o) =>
            !q ||
            (o.owner_name ?? "").toLowerCase().includes(q) ||
            (o.mobile ?? "").includes(q) ||
            (o.designation ?? "").toLowerCase().includes(q)
        )
        .sort((a, b) => (a.owner_name ?? "").localeCompare(b.owner_name ?? ""));

    // ── Paginate ─────────────────────────────────────────────────────
    const totalRecords = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const paginated = filtered.slice(startIdx, startIdx + pageSize);

    const goTo = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

    const getPageNumbers = () => {
        const pages = [];
        const left = Math.max(1, safePage - 2);
        const right = Math.min(totalPages, safePage + 2);
        for (let i = left; i <= right; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="cl-page">
            <div className="cl-panel">
                {/* ── Heading ── */}
                <div className="cl-panel-heading">
                    <span>Owner Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add Owner">
                        <Plus size={16} />
                    </button>
                </div>

                {/* ── Search ── */}
                <div className="cl-search-bar">
                    <input type="text" placeholder="Search by name, mobile, designation…" value={search}
                        onChange={(e) => setSearch(e.target.value)} className="cl-search-input" />
                    <button className="cl-refresh-btn" onClick={fetchOwners} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <>
                        <div className="cl-table-wrapper">
                            <table className="cl-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Owner ID</th>
                                        <th>Owner Name</th>
                                        <th>Mobile</th>
                                        <th>Designation</th>
                                        <th className="cl-actions-th">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length === 0 ? (
                                        <tr><td colSpan={6} className="cl-empty">No owners found.</td></tr>
                                    ) : (
                                        paginated.map((o, i) => (
                                            <tr
                                                key={o.owner_id ?? i}
                                                className={`${i % 2 === 0 ? "cl-row-even" : "cl-row-odd"} cl-row-clickable`}
                                                onClick={() => openEdit(o)}
                                                title="Click to edit"
                                            >
                                                <td data-label="#">{startIdx + i + 1}</td>
                                                <td data-label="Owner ID">{o.owner_id}</td>
                                                <td data-label="Owner Name">{o.owner_name}</td>
                                                <td data-label="Mobile">{o.mobile}</td>
                                                <td data-label="Designation">{o.designation}</td>
                                                <td className="cl-actions-td" onClick={(e) => e.stopPropagation()}>
                                                    <button className="cl-icon-btn cl-edit" onClick={() => openEdit(o)} title="Edit">
                                                        <Edit size={15} /><span className="cl-icon-label">Edit</span>
                                                    </button>
                                                    <button className="cl-icon-btn cl-delete" onClick={(e) => handleDelete(o, e)} title="Delete">
                                                        <Trash2 size={15} /><span className="cl-icon-label">Delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ── Pagination ── */}
                        <div className="cl-pagination">
                            <div className="cl-pagination-info">
                                {totalRecords === 0 ? "No records" : `Showing ${startIdx + 1}–${Math.min(startIdx + pageSize, totalRecords)} of ${totalRecords}`}
                            </div>
                            <div className="cl-pagination-controls">
                                <button className="cl-page-btn" onClick={() => goTo(1)} disabled={safePage === 1} title="First"><ChevronsLeft size={15} /></button>
                                <button className="cl-page-btn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1} title="Previous"><ChevronLeft size={15} /></button>
                                {getPageNumbers()[0] > 1 && (
                                    <><button className="cl-page-btn" onClick={() => goTo(1)}>1</button>
                                        {getPageNumbers()[0] > 2 && <span className="cl-page-ellipsis">…</span>}</>
                                )}
                                {getPageNumbers().map((p) => (
                                    <button key={p} className={`cl-page-btn ${p === safePage ? "cl-page-active" : ""}`} onClick={() => goTo(p)}>{p}</button>
                                ))}
                                {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                                    <>{getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && <span className="cl-page-ellipsis">…</span>}
                                        <button className="cl-page-btn" onClick={() => goTo(totalPages)}>{totalPages}</button></>
                                )}
                                <button className="cl-page-btn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages} title="Next"><ChevronRight size={15} /></button>
                                <button className="cl-page-btn" onClick={() => goTo(totalPages)} disabled={safePage === totalPages} title="Last"><ChevronsRight size={15} /></button>
                            </div>
                            <div className="cl-pagination-size">
                                <label>Rows:</label>
                                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                    {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                    </>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">{form.owner_id ? "Edit Owner" : "Add Owner"}</h4>
                            <button className="cl-modal-close" onClick={closeModal}><X size={18} /></button>
                        </div>

                        <div className="cl-modal-body">
                            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                                {/* Owner Name | Owner ID */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">Owner Name</label>
                                        <input type="text"
                                            className={`cl-form-control${errors.owner_name ? " cl-input-error" : ""}`}
                                            placeholder="Owner Name" maxLength={50}
                                            value={form.owner_name}
                                            onChange={(e) => set("owner_name", e.target.value)} />
                                        {errors.owner_name && <span className="cl-error-msg">{errors.owner_name}</span>}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">Owner ID</label>
                                        <input type="text" className="cl-form-control cl-readonly"
                                            placeholder="Auto" value={form.owner_id ?? ""} readOnly />
                                    </div>
                                </div>

                                {/* Mobile | Designation */}
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Mobile</label>
                                        <input type="tel" inputMode="numeric"
                                            className={`cl-form-control${errors.mobile ? " cl-input-error" : ""}`}
                                            placeholder="Mobile Number" maxLength={10}
                                            value={form.mobile}
                                            onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} />
                                        {errors.mobile && <span className="cl-error-msg">{errors.mobile}</span>}
                                    </div>
                                    <div className="cl-col-6">
                                        <label className="cl-control-label">Designation</label>
                                        <input type="text"
                                            className={`cl-form-control${errors.designation ? " cl-input-error" : ""}`}
                                            placeholder="e.g. Proprietor, Manager" maxLength={50}
                                            value={form.designation}
                                            onChange={(e) => set("designation", e.target.value)} />
                                        {errors.designation && <span className="cl-error-msg">{errors.designation}</span>}
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>Close</button>
                            <button className="cl-btn cl-btn-primary" onClick={onSave} disabled={saving}>
                                {saving ? "Saving…" : form.owner_id ? "Update" : "Add"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Owners;
